import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  MessageSquare, Mail, CheckCircle, XCircle, Send,
  ChevronLeft, ChevronRight, Filter
} from 'lucide-react';

const channelLabels = { whatsapp: 'WhatsApp', email: 'Email' };
const eventLabels = {
  order_created: 'Pesanan Baru',
  payment_success: 'Pembayaran Berhasil',
  payment_failed: 'Pembayaran Gagal',
  order_shipped: 'Pesanan Dikirim',
  order_delivered: 'Pesanan Diterima',
  low_stock: 'Stok Rendah',
  membership_active: 'Membership Aktif',
  course_enrolled: 'Pendaftaran Kursus',
  donation_received: 'Donasi Diterima',
  affiliate_commission: 'Komisi Affiliate',
  termin_reminder: 'Pengingat Termin',
  termin_overdue: 'Termin Jatuh Tempo',
  cod_confirmed: 'COD Dikonfirmasi',
  order_canceled: 'Pesanan Dibatalkan',
};

export default function NotificationLogs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [channelFilter, setChannelFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const limit = 20;

  document.title = 'Riwayat Notifikasi — Tokomoe';

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (channelFilter) params.channel = channelFilter;
      if (statusFilter) params.status = statusFilter;
      const { data } = await api.get('/tenant/notification-logs', { params });
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to fetch logs:', err);
    } finally {
      setLoading(false);
    }
  }, [page, channelFilter, statusFilter]);

  const fetchStats = async () => {
    try {
      const { data } = await api.get('/tenant/notification-logs/stats');
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  useEffect(() => { fetchStats(); }, []);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const totalPages = Math.ceil(total / limit);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Riwayat Notifikasi</h1>
        <p className="text-sm text-slate-500 mt-1">Log pengiriman WhatApp & Email ke pelanggan</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          <Card>
            <CardContent className="py-4 px-5 text-center">
              <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              <p className="text-xs text-slate-500 mt-1">Total Terkirim</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 px-5 text-center">
              <p className="text-2xl font-bold text-green-600">{stats.sent}</p>
              <p className="text-xs text-slate-500 mt-1">Berhasil</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 px-5 text-center">
              <p className="text-2xl font-bold text-red-600">{stats.failed}</p>
              <p className="text-xs text-slate-500 mt-1">Gagal</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 px-5 text-center">
              <p className="text-2xl font-bold text-green-500">{stats.whatsapp}</p>
              <p className="text-xs text-slate-500 mt-1">WhatsApp</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 px-5 text-center">
              <p className="text-2xl font-bold text-blue-500">{stats.email}</p>
              <p className="text-xs text-slate-500 mt-1">Email</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-1 text-sm text-slate-500">
          <Filter size={14} /> Filter:
        </div>
        <select value={channelFilter} onChange={e => { setChannelFilter(e.target.value); setPage(1); }}
          className="text-sm border border-slate-200 rounded-md px-3 py-1.5 bg-white">
          <option value="">Semua Channel</option>
          <option value="whatsapp">WhatsApp</option>
          <option value="email">Email</option>
        </select>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          className="text-sm border border-slate-200 rounded-md px-3 py-1.5 bg-white">
          <option value="">Semua Status</option>
          <option value="sent">Berhasil</option>
          <option value="failed">Gagal</option>
        </select>
      </div>

      {/* Logs Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" />
        </div>
      ) : logs.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Send size={48} className="text-slate-300 mb-4" />
            <p className="text-slate-500 text-lg font-medium">Belum ada riwayat notifikasi</p>
            <p className="text-slate-400 text-sm mt-1">Notifikasi WhatsApp & Email yang terkirim akan tampil di sini</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Waktu</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Event</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Channel</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Penerima</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-500">Pesan</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map(log => (
                    <tr key={log.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="py-3 px-4 text-slate-500 whitespace-nowrap">
                        {new Date(log.created_at).toLocaleString('id-ID', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-xs font-medium bg-slate-100 text-slate-700 px-2 py-1 rounded">
                          {eventLabels[log.event] || log.event}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="flex items-center gap-1.5">
                          {log.channel === 'whatsapp' ? (
                            <MessageSquare size={14} className="text-green-500" />
                          ) : (
                            <Mail size={14} className="text-blue-500" />
                          )}
                          {channelLabels[log.channel] || log.channel}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-700 max-w-[180px] truncate">
                        {log.recipient}
                      </td>
                      <td className="py-3 px-4">
                        {log.status === 'sent' ? (
                          <span className="flex items-center gap-1 text-green-600">
                            <CheckCircle size={14} /> Terkirim
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-red-600" title={log.error || ''}>
                            <XCircle size={14} /> Gagal
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-slate-500 max-w-[250px] truncate" title={log.message}>
                        {log.message}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <p className="text-sm text-slate-500">
            Menampilkan {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} dari {total} log
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}>
              <ChevronLeft size={16} />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}>
              <ChevronRight size={16} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
